import { centsToEuros } from '@/constants'
import { isAxiosError } from 'axios'
import type { useTranslation } from 'react-i18next'

// Backend context values that are integer cents and need to be rendered as a
// localised currency string in the translation interpolation.
const CURRENCY_CONTEXT_KEYS = new Set([
	'committed',
	'expected',
	'requested',
	'available',
	'minimum',
	'amount',
	'amount_remaining',
])

/**
 * Translate a backend `TranslatedException` ({ error, context }) into a
 * user-facing message via the `common:paymentErrors.<code>` i18n keys.
 *
 * Returns null when the error isn't a recognised payment error (not an Axios
 * error, no `error` code, or no matching translation) so callers can fall back
 * to a generic message.
 */
export function translatePaymentError(
	error: unknown,
	t: ReturnType<typeof useTranslation>['t'],
	currencyFormatter: Intl.NumberFormat,
): string | null {
	if (!isAxiosError(error)) { return null }
	const data = error.response?.data as { error?: unknown; context?: Record<string, unknown> } | undefined
	const code = data?.error
	if (typeof code !== 'string') { return null }

	const ctx: Record<string, unknown> = { ...(data?.context ?? {}) }
	for (const key of Object.keys(ctx)) {
		if (CURRENCY_CONTEXT_KEYS.has(key) && typeof ctx[key] === 'number') {
			ctx[key] = currencyFormatter.format(centsToEuros(ctx[key]))
		}
	}

	const key = `common:paymentErrors.${code}`
	const message = t(key, ctx)
	return message === key ? null : message
}
